/**
 * Wycena wywolan obrazowych na podstawie REJESTRU, nie hardkodu
 * (docs/BILLING.md § Rejestr modeli jako dane, § Reserve -> settle).
 *
 * Trzy modele cenowe OpenRoutera:
 *   - `per_image`      — flat za obraz, rozdzielczosc NIE wplywa na cene
 *                        (bytedance-seed/seedream-4.5: $0.04/obraz),
 *   - `per_megapixel`  — cena z policzonych megapikseli wyjscia
 *                        (black-forest-labs/flux.2-klein-4b: $0.014/MP),
 *   - `per_token`      — cena z estymaty tokenow wyjsciowych obrazu
 *                        (rodzina google/gemini-*-image).
 *
 * To sa ESTYMATY (`cost_ledger.usd_estimated`, state='reserved'). Prawda
 * przychodzi z `usage.cost` przy rozliczeniu (state='settled'). Rozjazd miedzy
 * jednym i drugim jest mierzony w czasie — dlatego estymata musi byc czysta
 * funkcja rejestru i parametrow, bez zgadywania w kroku Workflow.
 *
 * Zero DOM, zero Cloudflare. Zero `Date`, zero `Math.random()`.
 */

import type { ModelCapability, ModelPricing, PricingKind } from './capabilities';

// ─────────────────────────────────────────────────────────────────────────────
// Geometria wyjscia: resolution + aspect_ratio -> piksele -> megapiksele
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Dluzsza krawedz wyjscia dla tokenow rozdzielczosci uzywanych w `params`
 * (`resolution: '512' | '1K' | '2K' | '4K'`, docs/PROMPTING.md).
 *
 * Klucze sortowane leksykograficznie — plik jest czytany rowniez przez
 * generator dokumentacji cennika.
 */
export const RESOLUTION_LONG_EDGE_PX: Readonly<Record<string, number>> = {
  '1K': 1024,
  '2K': 2048,
  '4K': 4096,
  '8K': 8192,
  '256': 256,
  '512': 512,
  '768': 768,
  '1024': 1024,
  '1536': 1536,
  '2048': 2048,
  '4096': 4096,
};

/** Uzywane gdy wolajacy nie podal rozdzielczosci ani jej nie da sie sparsowac. */
export const DEFAULT_RESOLUTION = '1K';

/** Uzywane gdy wolajacy nie podal proporcji ani jej nie da sie sparsowac. */
export const DEFAULT_ASPECT_RATIO = '1:1';

/** Wymiary wyjscia sa zaokraglane do wielokrotnosci 8 px (wymog wiekszosci modeli). */
export const PIXEL_DIMENSION_MULTIPLE = 8;

export type PixelDimensions = {
  readonly width: number;
  readonly height: number;
};

/**
 * Dluzsza krawedz w pikselach dla tokenu rozdzielczosci.
 * `null` gdy token jest nieznany (np. nowy model wprowadzil '6K').
 */
export function resolutionLongEdgePx(resolution: string): number | null {
  const token = resolution.trim().toUpperCase();
  const known = RESOLUTION_LONG_EDGE_PX[token];
  if (known !== undefined) return known;
  // Zapis "1024x768" obsluguje pixelDimensionsFor; tutaj tylko goly wymiar.
  if (/^[0-9]{2,5}$/.test(token)) {
    const parsed = Number.parseInt(token, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  return null;
}

/** `'4:5'` -> `0.8` (szerokosc / wysokosc). `null` gdy zapis nieznany. */
export function parseAspectRatio(aspect: string): number | null {
  const match = /^\s*([0-9]{1,5})\s*[:x/]\s*([0-9]{1,5})\s*$/i.exec(aspect);
  if (match === null) return null;
  const w = Number.parseInt(match[1] ?? '', 10);
  const h = Number.parseInt(match[2] ?? '', 10);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return w / h;
}

/** Zaokraglenie do wielokrotnosci `PIXEL_DIMENSION_MULTIPLE`, minimum jeden krok. */
function roundToMultiple(value: number): number {
  const steps = Math.max(1, Math.round(value / PIXEL_DIMENSION_MULTIPLE));
  return steps * PIXEL_DIMENSION_MULTIPLE;
}

/**
 * Wymiary wyjscia: dluzsza krawedz rowna tokenowi rozdzielczosci, krotsza
 * wyliczona z proporcji. Obslugiwany jest tez jawny zapis `"1024x768"`
 * (wtedy proporcje sa ignorowane).
 */
export function pixelDimensionsFor(resolution: string, aspectRatio: string): PixelDimensions | null {
  const explicit = /^\s*([0-9]{2,5})\s*[x*]\s*([0-9]{2,5})\s*$/i.exec(resolution);
  if (explicit !== null) {
    const width = Number.parseInt(explicit[1] ?? '', 10);
    const height = Number.parseInt(explicit[2] ?? '', 10);
    if (width > 0 && height > 0) return { width, height };
    return null;
  }

  const longEdge = resolutionLongEdgePx(resolution);
  if (longEdge === null) return null;
  const ratio = parseAspectRatio(aspectRatio);
  if (ratio === null) return null;

  if (ratio >= 1) {
    // Poziomo albo kwadrat: dluzsza krawedz to szerokosc.
    return { width: longEdge, height: roundToMultiple(longEdge / ratio) };
  }
  return { width: roundToMultiple(longEdge * ratio), height: longEdge };
}

/**
 * Megapiksele wyjscia (1 MP = 1_000_000 px, konwencja cennikow per-MP).
 * `null` gdy rozdzielczosci albo proporcji nie da sie sparsowac.
 */
export function megapixelsFor(resolution: string, aspectRatio: string): number | null {
  const dims = pixelDimensionsFor(resolution, aspectRatio);
  if (dims === null) return null;
  return (dims.width * dims.height) / 1_000_000;
}

// ─────────────────────────────────────────────────────────────────────────────
// Estymata tokenow obrazu (cenniki per_token)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Tokeny wyjsciowe na megapiksel obrazu.
 *
 * Baza: rodzina gemini image rozlicza obraz do ~1 MP jako ~1290 tokenow
 * wyjsciowych. Skalujemy liniowo z powierzchnia — to estymata startowa dla
 * `usd_estimated`, rekoncyliowana potem po `usage.cost`.
 * TODO(spec): jesli endpoints OpenRoutera zaczna zwracac `tokens_per_image`,
 * czytac to z rejestru zamiast tej stalej.
 */
export const IMAGE_OUTPUT_TOKENS_PER_MEGAPIXEL = 1290;

/** Estymata tokenow promptu, gdy wolajacy jej nie zna (krotki prompt + refy). */
export const DEFAULT_PROMPT_TOKENS = 400;

/** Tokeny wyjsciowe dla obrazu o danej powierzchni. Deterministyczne, calkowite. */
export function estimateImageOutputTokens(megapixels: number): number {
  if (!Number.isFinite(megapixels) || megapixels <= 0) return 0;
  return Math.ceil(megapixels * IMAGE_OUTPUT_TOKENS_PER_MEGAPIXEL);
}

// ─────────────────────────────────────────────────────────────────────────────
// Estymata kosztu
// ─────────────────────────────────────────────────────────────────────────────

export type CostEstimateParams = {
  /** Liczba obrazow w tym wywolaniu (`n`). Domyslnie 1. */
  readonly n?: number;
  /** Token rozdzielczosci, np. `'512'`, `'2K'`. Domyslnie `DEFAULT_RESOLUTION`. */
  readonly resolution?: string;
  /** Proporcje, np. `'4:5'`. Domyslnie `DEFAULT_ASPECT_RATIO`. */
  readonly aspectRatio?: string;
  /** Znane albo estymowane tokeny promptu (cenniki per_token). */
  readonly promptTokens?: number;
  /** Znane tokeny wyjsciowe na obraz — nadpisuje estymate z megapikseli. */
  readonly outputTokensPerImage?: number;
};

/** Kwoty sa zaokraglane do 6 miejsc — inaczej `0.04 * 9` daje 0.36000000000000004. */
export const USD_PRECISION = 6;

/** Zaokraglenie kwoty USD do `USD_PRECISION` miejsc. Normalizuje `-0`. */
export function roundUsd(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** USD_PRECISION;
  const rounded = Math.round(value * factor) / factor;
  return rounded === 0 ? 0 : rounded;
}

export type CostEstimate = {
  readonly modelId: string;
  readonly pricingKind: PricingKind;
  readonly n: number;
  readonly resolution: string;
  readonly aspectRatio: string;
  readonly width: number | null;
  readonly height: number | null;
  readonly megapixels: number | null;
  /** Tylko dla `per_token`. */
  readonly promptTokens: number | null;
  /** Tylko dla `per_token` — na jeden obraz. */
  readonly outputTokensPerImage: number | null;
  /**
   * Koszt jednego obrazu. Dla `per_token` NIE zawiera kosztu promptu — ten
   * placi sie raz na wywolanie i siedzi w `usdTotal`.
   */
  readonly usdPerImage: number;
  /** Koszt calego wywolania: `n` obrazow (+ prompt raz, przy `per_token`). */
  readonly usdTotal: number;
  /** Przyjete zalozenia (nieznany token rozdzielczosci itp.). */
  readonly warnings: readonly string[];
};

/** Kolejnosc wariantow unii jest stabilna, wiec `switch` jest wyczerpujacy. */
function pricingKindOf(pricing: ModelPricing): PricingKind {
  return pricing.kind;
}

/**
 * Estymata kosztu jednego wywolania modelu obrazowego.
 *
 * Nie rzuca i nie zwraca `Result`: brakujace lub nieznane parametry sa
 * zastepowane udokumentowanymi domyslnymi i raportowane w `warnings`.
 * Pre-flight walidacja capability (n, refy, rozdzielczosc) nalezy do
 * `capabilityShortfalls` / `pickModel`, nie do wyceny.
 */
export function estimateCost(
  capability: ModelCapability,
  params: CostEstimateParams = {},
): CostEstimate {
  const warnings: string[] = [];

  const n = params.n === undefined ? 1 : Math.max(1, Math.trunc(params.n));

  let resolution = params.resolution ?? DEFAULT_RESOLUTION;
  let aspectRatio = params.aspectRatio ?? DEFAULT_ASPECT_RATIO;

  if (resolutionLongEdgePx(resolution) === null && pixelDimensionsFor(resolution, aspectRatio) === null) {
    warnings.push(`unknown resolution "${resolution}", assumed "${DEFAULT_RESOLUTION}"`);
    resolution = DEFAULT_RESOLUTION;
  }
  if (parseAspectRatio(aspectRatio) === null) {
    warnings.push(`unknown aspect ratio "${aspectRatio}", assumed "${DEFAULT_ASPECT_RATIO}"`);
    aspectRatio = DEFAULT_ASPECT_RATIO;
  }

  const dims = pixelDimensionsFor(resolution, aspectRatio);
  const megapixels = dims === null ? null : (dims.width * dims.height) / 1_000_000;

  const pricing = capability.pricing;
  let usdPerImage = 0;
  let usdTotal = 0;
  let promptTokens: number | null = null;
  let outputTokensPerImage: number | null = null;

  switch (pricing.kind) {
    case 'per_image': {
      // Flat: rozdzielczosc nie wplywa na cene (docs/PIPELINE.md, uwaga o seedreamie).
      usdPerImage = pricing.usdPerImage;
      usdTotal = usdPerImage * n;
      break;
    }
    case 'per_megapixel': {
      if (megapixels === null) {
        warnings.push('megapixels unknown, per-megapixel estimate is 0');
        usdPerImage = 0;
      } else {
        usdPerImage = pricing.usdPerMegapixel * megapixels;
      }
      usdTotal = usdPerImage * n;
      break;
    }
    case 'per_token': {
      promptTokens =
        params.promptTokens === undefined
          ? DEFAULT_PROMPT_TOKENS
          : Math.max(0, Math.trunc(params.promptTokens));
      if (params.promptTokens === undefined) {
        warnings.push(`prompt tokens unknown, assumed ${String(DEFAULT_PROMPT_TOKENS)}`);
      }
      if (params.outputTokensPerImage !== undefined) {
        outputTokensPerImage = Math.max(0, Math.trunc(params.outputTokensPerImage));
      } else if (megapixels === null) {
        warnings.push('megapixels unknown, output token estimate is 0');
        outputTokensPerImage = 0;
      } else {
        outputTokensPerImage = estimateImageOutputTokens(megapixels);
      }
      const imageTokenRate = pricing.usdPerImageToken ?? pricing.usdPerOutputToken;
      usdPerImage = outputTokensPerImage * imageTokenRate;
      usdTotal = usdPerImage * n + promptTokens * pricing.usdPerInputToken;
      break;
    }
  }

  return {
    modelId: capability.id,
    pricingKind: pricingKindOf(pricing),
    n,
    resolution,
    aspectRatio,
    width: dims === null ? null : dims.width,
    height: dims === null ? null : dims.height,
    megapixels,
    promptTokens,
    outputTokensPerImage,
    usdPerImage: roundUsd(usdPerImage),
    usdTotal: roundUsd(usdTotal),
    warnings,
  };
}

/**
 * Skrot: sam koszt wywolania w USD. To ta liczba idzie do
 * `cost_ledger.usd_estimated` i do kwotowania w UI PRZED klikiem.
 */
export function estimateCostUsd(
  capability: ModelCapability,
  params: CostEstimateParams = {},
): number {
  return estimateCost(capability, params).usdTotal;
}

/**
 * Koszt calej siatki, niezaleznie od tego czy poszla jednym wywolaniem `n=9`
 * czy dziewiecioma `n=1`. Tak wlasnie liczy sie "$0.36 vs $0.03" z UI.
 */
export function estimateGridCostUsd(
  capability: ModelCapability,
  callCount: number,
  perCallN: number,
  params: Omit<CostEstimateParams, 'n'> = {},
): number {
  const calls = Math.max(1, Math.trunc(callCount));
  const perCall = estimateCostUsd(capability, { ...params, n: perCallN });
  return roundUsd(perCall * calls);
}

/** Marza planu (`plans.markup_pct`) nalozona na koszt surowy. */
export function applyMarkup(usd: number, markupPct: number): number {
  const pct = Number.isFinite(markupPct) ? markupPct : 0;
  return roundUsd(usd * (1 + pct / 100));
}
